/**
 * Font Manager Utility
 * Handles dynamic font loading and template-specific font assignments
 */

// Font configuration for different templates
export const FONT_CONFIG = {
  'default': {
    primary: 'Inter',
    weights: [300, 400, 500, 600, 700, 800, 900],
    fallback: 'sans-serif'
  },
  'developer': {
    primary: 'Poppins',
    weights: [300, 400, 500, 600, 700, 800, 900],
    fallback: 'sans-serif'
  },
  'tyler': {
    primary: 'Montserrat',
    secondary: 'Source Code Pro',
    weights: [300, 400, 500, 600, 700, 800, 900],
    fallback: 'sans-serif'
  },
  'elegant': {
    primary: 'Playfair Display',
    weights: [400, 500, 600, 700, 800, 900],
    fallback: 'serif'
  },
  'modern': {
    primary: 'Roboto',
    weights: [300, 400, 500, 700, 900],
    fallback: 'sans-serif'
  }
};

/**
 * Apply template-specific font styling to the document
 * @param {string} templateName - The name of the template
 */
export const applyTemplateFont = (templateName) => {
  // Remove all existing template classes
  Object.keys(FONT_CONFIG).forEach(template => {
    document.body.classList.remove(`template-${template}`);
  });
  
  // Add the new template class
  if (FONT_CONFIG[templateName]) {
    document.body.classList.add(`template-${templateName}`);
  } else {
    // Fallback to default template
    document.body.classList.add('template-default');
  }
};

/**
 * Preload fonts for better performance
 * @param {string} templateName - The name of the template
 */
export const preloadTemplateFonts = (templateName) => {
  const config = FONT_CONFIG[templateName];
  if (!config) return;

  const fontFamily = config.primary;
  const weights = config.weights.join(';');
  
  // Create a link element to preload the font
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'font';
  link.type = 'font/woff2';
  link.crossOrigin = 'anonymous';
  link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(' ', '+')}:wght@${weights}&display=swap`;
  
  document.head.appendChild(link);
};

/**
 * Get font configuration for a template
 * @param {string} templateName - The name of the template
 * @returns {object} Font configuration object
 */
export const getFontConfig = (templateName) => {
  return FONT_CONFIG[templateName] || FONT_CONFIG['default'];
};

/**
 * Generate CSS font-family string for a template
 * @param {string} templateName - The name of the template
 * @returns {string} CSS font-family value
 */
export const getFontFamily = (templateName) => {
  const config = getFontConfig(templateName);
  return `'${config.primary}', ${config.fallback}`;
};
